// Pure reads behind Follow-ups (`/follow-ups`): every open scheduled activity the viewer can see, across stores, in
// four groups by due day. A read-only port of iOS TasksView (GroundTeam/Views/CalendarTasks/TasksView.swift and
// CalendarTasks+Store.swift `tasks(in:userIds:)`), with its Upcoming segment split into "Next 7 days" and "Later".
//
// Day logic is the one the dashboard's "Needs attention" and the store's Upcoming tab use: `isOverdue` (scheduled and
// its time has passed) and local calendar days from domain/calendar.ts (the browser's time zone, as an iPhone uses
// its own). Rows are worded like the store's Upcoming tab (stores/detailModel: tint, "Tomorrow 2:00 PM").
//
// Who sees what is the dashboard's "Needs attention" rule (dashboardModel.needsAttention): a rep gets only what is
// assigned to them, as iOS TasksView shows a rep (`agendaUserIds` is just themselves); managers get what is assigned to
// a visible person, plus unassigned work on a store they can see. Everything comes from the viewer's replica, so
// nothing the server held back can show, and the page never shows a rep more than the server's rules and iOS do.

import { ACTIVITY_TYPES, ACTIVITY_TYPE_TITLES, SCHEDULABLE_ACTIVITY_TYPES, type ActivityType } from '@/contracts/enums';
import type { ActivityPayload, RecordPayload, UserPayload, VisitResultPayload } from '@/contracts/models';
import { isUuid } from '@/contracts/uuid';
import { parseWireDate } from '@/contracts/wireDate';
import { startOfDayOffset } from '@/domain/calendar';
import { formatGrouped, formatRelative } from '@/domain/format';
import { activityDisplayTitle, isOverdue, recordDisplayName } from '@/domain/records';
import { compareLocalized, fullName, hasId, idKey, idSet, lookup, ms, sameId, trimAll } from '@/domain/util';
import { activityDateLine, activityTint } from '@/features/stores/detailModel';

/** An activity row as the list needs it (envelope id, best-effort value, newer-app flag). */
export interface FollowUpSource {
  id: string;
  value: ActivityPayload;
  undecodable?: string;
}

// MARK: - Scope

export interface FollowUpScope {
  /** `visibleUsers` ids (managers' people; reps: themselves). */
  visibleUserIds: Iterable<string>;
  /** The viewer's stores (`visibleRecords`, archived included: the store page opens those). */
  store: (id: string) => RecordPayload | undefined;
  /** The viewer's profile id. */
  viewerId: string | null | undefined;
  /** A rep's own list ("My follow-ups"): only what is assigned to them. */
  self: boolean;
}

/**
 * Open scheduled activities in the viewer's list, as "Needs attention" counts them: a rep's own; for managers, those
 * assigned to a visible person, or unassigned on one of the viewer's stores. A teammate's or an outsider's work on a
 * store the viewer can see stays on that store's Upcoming tab. Completed and cancelled activities never show.
 */
export function visibleFollowUps<T extends FollowUpSource>(items: readonly T[], scope: FollowUpScope): T[] {
  const people = idSet(scope.visibleUserIds);
  return items.filter(({ value: a }) => {
    if (a.status !== 'scheduled') return false;
    if (scope.self) return sameId(a.userId, scope.viewerId);
    if (a.userId) return hasId(people, a.userId);
    return !!a.recordId && scope.store(a.recordId) !== undefined;
  });
}

// MARK: - Groups

export const FOLLOW_UP_GROUPS = ['overdue', 'today', 'week', 'later'] as const;
export type FollowUpGroupId = (typeof FOLLOW_UP_GROUPS)[number];

export const FOLLOW_UP_GROUP_TITLES: Readonly<Record<FollowUpGroupId, string>> = {
  overdue: 'Overdue',
  today: 'Today',
  week: 'Next 7 days',
  later: 'Later',
};

/** What an empty group says (the iOS TasksView empty states, shortened to one line). */
export const FOLLOW_UP_GROUP_EMPTY: Readonly<Record<FollowUpGroupId, string>> = {
  overdue: 'Nothing overdue. Every scheduled activity is on track.',
  today: 'Nothing else due today.',
  week: 'Nothing scheduled for the next 7 days.',
  later: 'Nothing scheduled further out.',
};

/**
 * The group of a scheduled activity at `now`:
 * - Overdue: its time has passed (`isOverdue`, so 2 PM today is overdue at 3 PM);
 * - Today: later today, or no time set (iOS lists those under Today as "Anytime");
 * - Next 7 days: from tomorrow's midnight up to the midnight 8 days from now (the 7 calendar days after today);
 * - Later: everything after that.
 */
export function followUpGroupOf(a: Pick<ActivityPayload, 'status' | 'scheduledAt'>, now: Date): FollowUpGroupId {
  if (isOverdue(a, now)) return 'overdue';
  const at = ms(a.scheduledAt);
  if (at === undefined || at < startOfDayOffset(now, 1).getTime()) return 'today';
  if (at < startOfDayOffset(now, 8).getTime()) return 'week';
  return 'later';
}

export interface FollowUpRow {
  /** Envelope id. */
  id: string;
  type: ActivityType;
  /** "On-Site Visit", "Call · Left voicemail", the task's title. */
  title: string;
  /** Badge color: the visit result's color or the type tint (ActivityRow). */
  tint: string;
  /** The store's name, "Store not available" (not one of the viewer's stores) or "No store". */
  storeLabel: string;
  /** The store's Upcoming tab, only for a store the viewer can open. */
  href?: string;
  /** The assignee (none: unassigned, or a profile that has not synced). */
  user?: UserPayload;
  /** Undefined when no time is set. */
  dueAt?: Date;
  /** "Today 2:00 PM", "Tomorrow 9:00 AM", "Oct 20, 2026 9:00 AM"; "Anytime" without a time. */
  dueText: string;
  overdue: boolean;
  /** Overdue rows: "Due 3 days ago". */
  overdueText?: string;
  /** The task's notes, trimmed (shown on one line). */
  notes?: string;
  /** Written by a newer app (shown with the "Newer app" badge). */
  newer: boolean;
}

export interface FollowUpGroup {
  id: FollowUpGroupId;
  title: string;
  rows: FollowUpRow[];
}

export interface FollowUpRowContext {
  now: Date;
  /** The viewer's stores (archived included), by any-case id. */
  store: (id: string) => RecordPayload | undefined;
  users: ReadonlyMap<string, UserPayload>;
  visitResults: ReadonlyMap<string, VisitResultPayload>;
  locale?: string;
}

/** One list row: the store's Upcoming wording plus where it is and who has it. */
export function followUpRow(source: FollowUpSource, ctx: FollowUpRowContext): FollowUpRow {
  const a = source.value;
  const at = parseWireDate(a.scheduledAt);
  const record = a.recordId ? ctx.store(a.recordId) : undefined;
  const overdue = isOverdue(a, ctx.now);
  const row: FollowUpRow = {
    id: source.id,
    type: a.type,
    title: activityDisplayTitle(a, ctx.visitResults),
    tint: activityTint(a, ctx.visitResults),
    storeLabel: record ? recordDisplayName(record) : a.recordId ? 'Store not available' : 'No store',
    dueText: at ? activityDateLine(a, ctx.now, ctx.locale) : 'Anytime',
    overdue,
    newer: source.undecodable !== undefined,
  };
  // Link with the id that resolved: the store page looks it up the same way (any case).
  if (record && a.recordId) row.href = `/stores/${a.recordId}?tab=upcoming`;
  const user = lookup(ctx.users, a.userId);
  if (user) row.user = user;
  if (at) row.dueAt = at;
  if (overdue && at) row.overdueText = `Due ${formatRelative(at, ctx.now, ctx.locale)}`;
  const notes = trimAll(a.notes);
  if (notes !== '') row.notes = notes;
  return row;
}

/**
 * The four groups in order, each soonest first (Overdue: the oldest first, like "Needs attention"; no time last, as
 * the store's Upcoming tab sorts). Every group is returned, empty or not, so the page keeps its shape.
 */
export function followUpGroups(list: readonly FollowUpSource[], ctx: FollowUpRowContext): FollowUpGroup[] {
  const buckets: Record<FollowUpGroupId, FollowUpSource[]> = { overdue: [], today: [], week: [], later: [] };
  for (const s of list) buckets[followUpGroupOf(s.value, ctx.now)].push(s);
  const due = (s: FollowUpSource) => ms(s.value.scheduledAt) ?? Infinity;
  return FOLLOW_UP_GROUPS.map((id) => ({
    id,
    title: FOLLOW_UP_GROUP_TITLES[id],
    rows: buckets[id].sort((x, y) => due(x) - due(y) || 0).map((s) => followUpRow(s, ctx)),
  }));
}

/** "14 scheduled · 3 overdue" (the overdue part only when there are some). */
export function followUpSummary(groups: readonly FollowUpGroup[], locale?: string): string {
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  const overdue = groups.find((g) => g.id === 'overdue')?.rows.length ?? 0;
  const parts = [`${formatGrouped(total, locale)} scheduled`];
  if (overdue > 0) parts.push(`${formatGrouped(overdue, locale)} overdue`);
  return parts.join(' · ');
}

// MARK: - Filters

/** `?rep=` value for activities nobody is assigned to. */
export const UNASSIGNED_REP = 'unassigned';

export interface FollowUpFilter {
  /** A person's id, `UNASSIGNED_REP`, or null for everyone. */
  rep: string | null;
  /** One activity type, or null for all. */
  type: ActivityType | null;
}

export const NO_FOLLOW_UP_FILTER: FollowUpFilter = { rep: null, type: null };

/** Reads `?rep=`: a profile id or "unassigned"; anything else is everyone. */
export function parseRepParam(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.toLowerCase() === UNASSIGNED_REP) return UNASSIGNED_REP;
  return isUuid(value) ? value : null;
}

/** Reads `?type=`: an activity type; anything else is all types. */
export function parseTypeParam(value: string | null | undefined): ActivityType | null {
  return value && (ACTIVITY_TYPES as readonly string[]).includes(value) ? (value as ActivityType) : null;
}

export function followUpMatches(a: Pick<ActivityPayload, 'userId' | 'type'>, filter: FollowUpFilter): boolean {
  if (filter.type !== null && a.type !== filter.type) return false;
  if (filter.rep === null) return true;
  if (filter.rep === UNASSIGNED_REP) return !a.userId;
  return sameId(a.userId, filter.rep);
}

export function filterFollowUps<T extends FollowUpSource>(list: readonly T[], filter: FollowUpFilter): T[] {
  if (filter.rep === null && filter.type === null) return [...list];
  return list.filter((s) => followUpMatches(s.value, filter));
}

export interface RepOption {
  kind: 'everyone' | 'me' | 'person' | 'unassigned';
  /** The `rep` filter value; null for Everyone. */
  value: string | null;
  label: string;
  inactive: boolean;
}

/**
 * The rep menu (managers; iOS `agendaReps` plus Me and Whole team): Everyone, then the viewer's own, then the active
 * visible people by name (`pickerUsers`) and anyone else who still has something scheduled here (an inactive rep's
 * leftovers), then Unassigned when something is. The current choice always stays listed.
 */
export function repOptions(input: {
  /** `pickerUsers(visibleUsers)`: active visible people by name. */
  people: readonly UserPayload[];
  /** The follow-ups in scope (before filtering). */
  list: readonly FollowUpSource[];
  users: ReadonlyMap<string, UserPayload>;
  viewerId: string | null | undefined;
  selected: string | null;
}): RepOption[] {
  const out: RepOption[] = [{ kind: 'everyone', value: null, label: 'Everyone', inactive: false }];
  if (input.viewerId) out.push({ kind: 'me', value: input.viewerId, label: 'My follow-ups', inactive: false });
  const listed = idSet(input.viewerId ? [input.viewerId] : []);
  const add = (u: UserPayload) => {
    if (hasId(listed, u.id)) return;
    listed.add(idKey(u.id));
    out.push({ kind: 'person', value: u.id, label: fullName(u) || 'Unnamed', inactive: !u.isActive });
  };
  for (const u of input.people) add(u);
  const extra: UserPayload[] = [];
  let unassigned = false;
  for (const { value: a } of input.list) {
    if (!a.userId) {
      unassigned = true;
      continue;
    }
    const u = lookup(input.users, a.userId);
    if (u && !hasId(listed, u.id) && !extra.some((e) => sameId(e.id, u.id))) extra.push(u);
  }
  if (input.selected && input.selected !== UNASSIGNED_REP) {
    const u = lookup(input.users, input.selected);
    if (u && !hasId(listed, u.id) && !extra.some((e) => sameId(e.id, u.id))) extra.push(u);
  }
  extra.sort((a, b) => compareLocalized(fullName(a) || 'Unnamed', fullName(b) || 'Unnamed'));
  for (const u of extra) add(u);
  if (unassigned || input.selected === UNASSIGNED_REP) {
    out.push({ kind: 'unassigned', value: UNASSIGNED_REP, label: 'Unassigned', inactive: false });
  }
  return out;
}

/** The rep button's text: "Everyone", "My follow-ups", a name, "Unassigned". */
export function repFilterLabel(
  rep: string | null,
  viewerId: string | null | undefined,
  users: ReadonlyMap<string, UserPayload>,
): string {
  if (rep === null) return 'Everyone';
  if (rep === UNASSIGNED_REP) return 'Unassigned';
  if (sameId(rep, viewerId)) return 'My follow-ups';
  const u = lookup(users, rep);
  return u ? fullName(u) || 'Unnamed' : 'Unknown person';
}

/** `AgendaSupport.groupOrder`: tasks first, then field work, then messaging. */
const TYPE_ORDER: readonly ActivityType[] = ['task', 'visit', 'meeting', 'call', 'email', 'text', 'note', 'photo', 'document'];

/** `AgendaSupport.pluralTitle`. */
export const ACTIVITY_TYPE_PLURALS: Readonly<Record<ActivityType, string>> = {
  visit: 'Visits',
  call: 'Calls',
  email: 'Emails',
  text: 'Texts',
  note: 'Notes',
  meeting: 'Meetings',
  task: 'Tasks',
  photo: 'Photos',
  document: 'Documents',
  stageChange: 'Stage changes',
  assignment: 'Assignments',
  created: 'Created',
};

function typeRank(t: ActivityType): number {
  const i = TYPE_ORDER.indexOf(t);
  return i >= 0 ? i : TYPE_ORDER.length + ACTIVITY_TYPES.indexOf(t);
}

/**
 * The type menu: every type that can be scheduled (`ActivityType.schedulable`), plus any other type something in
 * the list has (an autoplay step), plus the current choice, in the iOS group order.
 */
export function typeOptions(list: readonly FollowUpSource[], selected: ActivityType | null): ActivityType[] {
  const types = new Set<ActivityType>(SCHEDULABLE_ACTIVITY_TYPES);
  for (const s of list) types.add(s.value.type);
  if (selected) types.add(selected);
  return [...types].sort((a, b) => typeRank(a) - typeRank(b));
}

/** The type button's text: "All types" or the plural ("Visits"). */
export function typeFilterLabel(type: ActivityType | null): string {
  return type === null ? 'All types' : (ACTIVITY_TYPE_PLURALS[type] ?? ACTIVITY_TYPE_TITLES[type]);
}
